/**
 * MediaLightbox — fullscreen fotoviewer voor de eigen media-laag, zelfde UX
 * als de klant-timeline (client-app api/fotos/[token].ts): horizontale
 * scroll-snap-strip (swipen op touch), pijlen/pijltjestoetsen, teller, Esc
 * en klik-naast-de-foto sluiten. Alleen foto's; video's spelen inline in de
 * grid.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface LightboxFoto {
  id: string;
  url: string;
  /** Onderschrift in de teller-pill, bv. "Foto 3 · Voorinspectie · 06-08-2026" */
  label: string;
}

interface Props {
  fotos: LightboxFoto[];
  start: number;
  onClose: () => void;
  /** Optioneel: verwijderen van de huidige foto (kantoor-correctie). */
  onDelete?: (fotoId: string) => void;
}

function TrashIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
      />
    </svg>
  );
}

export function MediaLightbox({ fotos, start, onClose, onDelete }: Props) {
  const stripRef = useRef<HTMLDivElement>(null);
  const rustRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [idx, setIdx] = useState(() => Math.max(0, Math.min(fotos.length - 1, start)));

  // idx is de waarheid bij knop/toets-navigatie; puur uit scrollLeft afleiden
  // mist stappen bij snel doorklikken. Swipes zetten idx bij zodra het
  // scrollen tot rust komt (debounce).
  const toon = useCallback(
    (i: number, direct = false) => {
      const strip = stripRef.current;
      if (!strip) return;
      const doel = Math.max(0, Math.min(fotos.length - 1, i));
      setIdx(doel);
      strip.scrollTo({ left: doel * strip.clientWidth, behavior: direct ? 'instant' : 'smooth' });
    },
    [fotos.length]
  );

  useEffect(() => {
    toon(start, true);
    const vorige = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const opToets = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') toon(stripIdx() - 1);
      if (e.key === 'ArrowRight') toon(stripIdx() + 1);
    };
    // Huidige index uit de DOM zodat de listener niet per idx opnieuw bindt.
    const stripIdx = () => {
      const strip = stripRef.current;
      return strip ? Math.round(strip.scrollLeft / strip.clientWidth) : 0;
    };
    window.addEventListener('keydown', opToets);
    return () => {
      document.body.style.overflow = vorige;
      window.removeEventListener('keydown', opToets);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const opScroll = () => {
    if (rustRef.current) clearTimeout(rustRef.current);
    rustRef.current = setTimeout(() => {
      const strip = stripRef.current;
      if (strip) setIdx(Math.round(strip.scrollLeft / strip.clientWidth));
    }, 90);
  };

  const huidige = fotos[Math.max(0, Math.min(fotos.length - 1, idx))];
  if (!huidige) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/95" role="dialog" aria-modal="true" aria-label="Foto's">
      <div className="fixed top-3 right-3 z-10 flex items-center gap-2">
        {onDelete && (
          <button
            type="button"
            onClick={() => onDelete(huidige.id)}
            aria-label="Foto verwijderen"
            title="Verwijderen"
            className="w-10 h-10 rounded-full bg-white/15 hover:bg-red-600 text-white flex items-center justify-center backdrop-blur-sm"
          >
            <TrashIcon />
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Sluiten"
          className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white text-2xl leading-none backdrop-blur-sm"
        >
          &times;
        </button>
      </div>

      <div
        ref={stripRef}
        onScroll={opScroll}
        className="flex h-full w-full overflow-x-auto snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none' }}
      >
        {fotos.map((f) => (
          <div
            key={f.id}
            onClick={onClose}
            className="flex h-full w-full flex-none snap-center snap-always items-center justify-center px-3 pt-12 pb-16"
          >
            <img
              src={f.url}
              alt={f.label}
              loading="lazy"
              onClick={(e) => e.stopPropagation()}
              className="max-h-full max-w-full object-contain rounded-lg"
            />
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => toon(idx - 1)}
        aria-label="Vorige foto"
        className="hidden sm:block fixed left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white text-3xl leading-none backdrop-blur-sm"
      >
        &#8249;
      </button>
      <button
        type="button"
        onClick={() => toon(idx + 1)}
        aria-label="Volgende foto"
        className="hidden sm:block fixed right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white text-3xl leading-none backdrop-blur-sm"
      >
        &#8250;
      </button>

      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-10 px-4 py-1.5 rounded-full bg-white/15 text-white text-[13px] font-medium backdrop-blur-sm whitespace-nowrap">
        {huidige.label} · {idx + 1} / {fotos.length}
      </div>
    </div>
  );
}
